import { gql } from "@apollo/client";

import { CommandResultPayload } from "src/store/graphql/helpers/commandResult.graphql";

// Hand-rolled mutation documents for the announcement module (see the note in
// query.graphql.ts). Every mutation resolves to the library CommandResult
// shape: { status, message }.

export interface AnnouncementInput {
  title: string;
  message: string;
  link: string | null;
  linkLabel: string | null;
  startDate: string;
  endDate: string;
  highlight: boolean;
  /** EVERYONE ignores both recipient lists; TARGETED needs at least one across them. */
  audience: "EVERYONE" | "TARGETED";
  /** Tenants to reach; every user of them receives it. */
  tenantIds: string[];
  /** People to reach individually, on top of the tenants. */
  userIds: string[];
}

export interface TCreateAnnouncementMutation {
  announcement: { createAnnouncement: CommandResultPayload };
}

export const createAnnouncementMutation = gql`
  mutation createAnnouncement($input: AnnouncementInput!) {
    announcement {
      createAnnouncement(input: $input) {
        status
        message
      }
    }
  }
`;

export interface TUpdateAnnouncementMutation {
  announcement: { updateAnnouncement: CommandResultPayload };
}

export const updateAnnouncementMutation = gql`
  mutation updateAnnouncement($id: ID!, $input: AnnouncementInput!) {
    announcement {
      updateAnnouncement(id: $id, input: $input) {
        status
        message
      }
    }
  }
`;

export interface TDeleteAnnouncementMutation {
  announcement: { deleteAnnouncement: CommandResultPayload };
}

export const deleteAnnouncementMutation = gql`
  mutation deleteAnnouncement($id: ID!) {
    announcement {
      deleteAnnouncement(id: $id) {
        status
        message
      }
    }
  }
`;

export interface TMarkAnnouncementReadMutation {
  announcement: { markAnnouncementRead: CommandResultPayload };
}

export const markAnnouncementReadMutation = gql`
  mutation markAnnouncementRead($id: ID!) {
    announcement {
      markAnnouncementRead(id: $id) {
        status
        message
      }
    }
  }
`;

export interface TMarkAnnouncementsReadMutation {
  announcement: { markAnnouncementsRead: CommandResultPayload };
}

export const markAnnouncementsReadMutation = gql`
  mutation markAnnouncementsRead($ids: [ID!]!) {
    announcement {
      markAnnouncementsRead(ids: $ids) {
        status
        message
      }
    }
  }
`;

export interface TMarkAllAnnouncementsReadMutation {
  announcement: { markAllAnnouncementsRead: CommandResultPayload };
}

export const markAllAnnouncementsReadMutation = gql`
  mutation markAllAnnouncementsRead {
    announcement {
      markAllAnnouncementsRead {
        status
        message
      }
    }
  }
`;

export interface TRegisterAnnouncementClickMutation {
  announcement: { registerAnnouncementClick: CommandResultPayload };
}

export const registerAnnouncementClickMutation = gql`
  mutation registerAnnouncementClick($id: ID!) {
    announcement {
      registerAnnouncementClick(id: $id) {
        status
        message
      }
    }
  }
`;

export interface TDismissAnnouncementMutation {
  announcement: { dismissAnnouncement: CommandResultPayload };
}

export const dismissAnnouncementMutation = gql`
  mutation dismissAnnouncement($id: ID!) {
    announcement {
      dismissAnnouncement(id: $id) {
        status
        message
      }
    }
  }
`;

export interface TGrantAnnouncementManagerMutation {
  announcement: { grantAnnouncementManager: CommandResultPayload };
}

export const grantAnnouncementManagerMutation = gql`
  mutation grantAnnouncementManager($userId: ID!) {
    announcement {
      grantAnnouncementManager(userId: $userId) {
        status
        message
      }
    }
  }
`;

export interface TRevokeAnnouncementManagerMutation {
  announcement: { revokeAnnouncementManager: CommandResultPayload };
}

export const revokeAnnouncementManagerMutation = gql`
  mutation revokeAnnouncementManager($userId: ID!) {
    announcement {
      revokeAnnouncementManager(userId: $userId) {
        status
        message
      }
    }
  }
`;
